import { useCallback, useEffect, useRef, useState } from "react";

import { useRouter } from "next/router";

import { StripeDetails } from "@/components/Account/payment/payment.presentation";
import {
  STRIPE_RETURN_GIVE_UP_MS,
  STRIPE_RETURN_MAX_MS,
  STRIPE_RETURN_PARAM,
  STRIPE_RETURN_POLL_MS,
  STRIPE_RETURN_SLOW_POLL_MS,
} from "@/components/Account/payment/payment.static";
import {
  StripeLinkType,
  linkTypeForRefresh,
} from "@/components/Account/payment/payment.utils";
import {
  StripeReturnBaseline,
  clearStripeReturnBaseline,
  readStripeReturnBaseline,
} from "@/components/Account/payment/useStripeRedirect";

/** Drops one query parameter from the URL without a navigation. */
export const useClearQueryParam = () => {
  const router = useRouter();

  return useCallback(
    (param: string) => {
      const { [param]: _removed, ...rest } = router.query;

      void router.replace(
        { pathname: router.pathname, query: rest },
        undefined,
        { shallow: true }
      );
    },
    [router]
  );
};

type StripeReturnInput = {
  /** `?stripe_return=1` is in the URL. */
  active: boolean;
  details: StripeDetails | null;
  startPolling: (intervalMs: number) => void;
  stopPolling: () => void;
};

/**
 * Back from Stripe (spec state R): poll the status every few seconds until
 * it moved from the one the seller left with. After a minute the waiting
 * card gives way to whatever the gateway says, with a panel that Stripe is
 * still checking (`timedOut`), and the poll slows to every half minute for
 * up to ten minutes. The page never overrides the gateway's status: a pinned
 * "Being checked" next to a READY title is worse than an honest wait.
 */
export const useStripeReturn = ({
  active,
  details,
  startPolling,
  stopPolling,
}: StripeReturnInput) => {
  const clearQueryParam = useClearQueryParam();
  const [fetching, setFetching] = useState(active);
  const [timedOut, setTimedOut] = useState(false);
  const baselineRef = useRef<StripeReturnBaseline | null | undefined>(
    undefined
  );

  useEffect(() => {
    if (!active) {
      return;
    }

    startPolling(STRIPE_RETURN_POLL_MS);
    const slowDown = window.setTimeout(() => {
      setTimedOut(true);
      setFetching(false);
      startPolling(STRIPE_RETURN_SLOW_POLL_MS);
    }, STRIPE_RETURN_MAX_MS);
    const giveUp = window.setTimeout(() => {
      stopPolling();
      clearStripeReturnBaseline();
      clearQueryParam(STRIPE_RETURN_PARAM);
    }, STRIPE_RETURN_GIVE_UP_MS);

    return () => {
      stopPolling();
      window.clearTimeout(slowDown);
      window.clearTimeout(giveUp);
    };
    // Only the arrival of ?stripe_return=1 starts the poll; the router
    // callback changes identity on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  useEffect(() => {
    if (!active || !details) {
      return;
    }

    if (baselineRef.current === undefined) {
      // The status the seller left with, remembered before the redirect;
      // without it the first response is the baseline and the next poll
      // decides.
      baselineRef.current = readStripeReturnBaseline() ?? {
        status: details.account_status,
        asOf: details.verification_as_of ?? null,
      };
    }

    const baseline = baselineRef.current;
    const moved =
      baseline !== null &&
      (baseline.status !== details.account_status ||
        baseline.asOf !== (details.verification_as_of ?? null));

    if (moved) {
      stopPolling();
      setFetching(false);
      setTimedOut(false);
      clearStripeReturnBaseline();
      clearQueryParam(STRIPE_RETURN_PARAM);
    }
  }, [active, clearQueryParam, details, stopPolling]);

  return { fetching, timedOut };
};

type StripeRefreshInput = {
  /** `?stripe_refresh=1` is in the URL: the Account Link expired or was used. */
  active: boolean;
  detailsSubmitted: boolean | null;
  redirect: (linkType: StripeLinkType) => Promise<void>;
};

/** An expired or used Account Link lands here: mint a new one at once, once. */
export const useStripeRefresh = ({
  active,
  detailsSubmitted,
  redirect,
}: StripeRefreshInput) => {
  const refreshedRef = useRef(false);

  useEffect(() => {
    if (!active || detailsSubmitted === null || refreshedRef.current) {
      return;
    }

    refreshedRef.current = true;
    void redirect(linkTypeForRefresh(detailsSubmitted));
  }, [active, detailsSubmitted, redirect]);
};
